// Badges "?" sobre cada palabra VÁLIDA del tablero. Al pulsarlos, muestra la definición
// de la RAE (API no oficial rae-api.com) en un popup centrado. Solo aparecen si hay
// internet (navigator.onLine); si no, no se dibuja el círculo amarillo.
import { Vector3 } from 'three';
import { EVENTS } from '../utils/constants.js';

const RAE_URL = 'https://rae-api.com/api/words/';

export class WordDefsOverlay {
  /** @param {object} deps { root, bus, sceneManager, modals, getGame, getScene } */
  constructor({ root, bus, sceneManager, modals, getGame, getScene }) {
    this.bus = bus;
    this.sm = sceneManager;
    this.modals = modals;
    this.getGame = getGame;
    this.getScene = getScene;
    this._v = new Vector3();
    this._cache = new Map(); // palabra(min) -> array de acepciones (o null si no existe)

    this.layer = document.createElement('div');
    this.layer.className = 'word-defs-layer';
    root.appendChild(this.layer);
    this.badges = new Map(); // wordId -> botón

    // Recalcula qué palabras son válidas cuando cambia el tablero / turno; limpia al acabar.
    this._offs = [
      bus.on(EVENTS.WORD_VALIDATED, () => this._rebuild()),
      bus.on(EVENTS.BOARD_CHANGED, () => this._rebuild()),
      bus.on(EVENTS.TURN_START, () => this._rebuild()),
      bus.on(EVENTS.GAME_END, () => this.clear()),
    ];
    this._onConn = () => this._rebuild();
    window.addEventListener('online', this._onConn);
    window.addEventListener('offline', this._onConn);
    // Cada frame: reposiciona los badges sobre sus palabras (siguen el paneo/zoom).
    this.sm.addUpdater(() => this._reposition());
  }

  clear() {
    for (const el of this.badges.values()) el.remove();
    this.badges.clear();
  }

  /** Reconstruye el conjunto de badges: uno por palabra VÁLIDA (solo con internet). */
  _rebuild() {
    const game = this.getGame?.();
    const online = navigator.onLine;
    this.layer.style.display = online ? '' : 'none';
    if (!online || !game || !game.state || game.state.finished || !game.board) { this.clear(); return; }

    const valid = new Set();
    for (const w of game.board.words) {
      if (!game.validator.validateWord(w.tiles).valid) continue;
      valid.add(w.id);
      let el = this.badges.get(w.id);
      if (!el) { el = this._makeBadge(w.id); this.badges.set(w.id, el); }
      el._word = w; // referencia viva para texto y posición
    }
    for (const [id, el] of [...this.badges]) {
      if (!valid.has(id)) { el.remove(); this.badges.delete(id); }
    }
    this._reposition();
  }

  _makeBadge(id) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'word-def-badge';
    el.textContent = '?';
    el.setAttribute('aria-label', 'Ver definición');
    el.title = 'Ver definición (RAE)';
    // Evita que el toque llegue al tablero (arrastre de fichas).
    el.addEventListener('pointerdown', (e) => e.stopPropagation());
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const w = el._word;
      const game = this.getGame?.();
      if (w && game) this._showDefinition(game.board.wordText(w).toUpperCase());
    });
    this.layer.appendChild(el);
    return el;
  }

  /** Reposiciona cada badge en la ESQUINA SUPERIOR DERECHA del contorno de su palabra
   *  (centrado en la esquina: mitad dentro, mitad fuera). */
  _reposition() {
    if (!this.badges.size) return;
    const scene = this.getScene?.();
    const cam = this.sm?.camera;
    if (!scene || !cam) return;
    const W = window.innerWidth, H = window.innerHeight;
    for (const [id, el] of this.badges) {
      let cx, cz; // esquina sup-derecha del contorno en mundo (x máx, z mín = arriba en pantalla)
      const outline = scene.rowLabels?.get(id); // recuadro (verde) que abraza la palabra
      if (outline && outline.userData?.hw != null) {
        cx = outline.position.x + outline.userData.hw;
        cz = outline.position.z - outline.userData.hd;
      }
      if (cx === undefined) { // respaldo: ficha más a la derecha
        let m = null, maxX = -Infinity;
        for (const t of (el._word?.tiles || [])) {
          const mm = scene.meshes.get(t.instanceId);
          if (mm && mm.position.x > maxX) { maxX = mm.position.x; m = mm; }
        }
        if (m) { cx = m.position.x; cz = m.position.z; }
      }
      if (cx === undefined) { el.style.display = 'none'; continue; }
      this._v.set(cx, 0.02, cz).project(cam);
      if (this._v.z > 1) { el.style.display = 'none'; continue; } // detrás de cámara
      el.style.display = '';
      el.style.left = `${(this._v.x * 0.5 + 0.5) * W}px`;
      el.style.top = `${(-this._v.y * 0.5 + 0.5) * H}px`;
    }
  }

  // --- Definición RAE ---
  async _showDefinition(wordText) {
    const modal = this.modals.open({
      title: wordText,
      className: 'def-modal',
      bodyHTML: '<p class="def-status">Buscando definición…</p>',
      dismissible: true,
    });
    // Cerrar con círculo + X arriba a la derecha (como el resto de ventanas).
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);
    const setBody = (html) => { const s = modal.querySelector('.modal-scale'); if (s) s.innerHTML = html; this.modals._fit?.(modal); };
    const key = wordText.toLowerCase();
    try {
      let senses = this._cache.get(key);
      if (senses === undefined) {
        const r = await fetch(RAE_URL + encodeURIComponent(key));
        senses = r.ok ? this._parseSenses(await r.json()) : null;
        this._cache.set(key, senses);
      }
      if (!senses || !senses.length) {
        setBody('<p class="def-status">No se encontró la definición en la RAE.</p>');
        return;
      }
      setBody(`<div class="def-list">${senses.slice(0, 20).map((s) => `<div class="def-item">${s}</div>`).join('')}</div>`);
    } catch {
      setBody('<p class="def-status">No se pudo conectar con la RAE.<br>Comprueba tu conexión.</p>');
    }
  }

  _parseSenses(json) {
    const raws = (json?.data?.meanings || []).flatMap((m) => m.senses || []).map((s) => s.raw).filter(Boolean);
    return raws.map((raw) => this._cleanSense(raw));
  }

  _cleanSense(raw) {
    let s = String(raw).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Separa sinónimos/antónimos a su propia línea para que se lea mejor.
    s = s.replace(/\s*(Sin\.:|Ant\.:)/g, '<br><i>$1</i> ');
    return s;
  }
}
